import { useEffect, useRef, useState } from 'react'
import 'flag-icons/css/flag-icons.min.css'
import { LANGS, useI18n } from '../lib/i18n'

/** Flag dropdown with native language names. flag-icons CSS (never emoji —
 *  Windows doesn't render emoji flags). */
export default function LangSwitcher({ light }: { light: boolean }) {
  const { lang, setLang } = useI18n()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const current = LANGS.find((l) => l.code === lang) ?? LANGS[0]

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
        className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold transition-colors ${
          light
            ? 'border-[#dfe5ef] bg-white text-[#071023] hover:border-[#ffb21f]'
            : 'border-ink-800 bg-ink-900 text-ink-100 hover:border-brand-500/60'
        }`}
      >
        <span className={`fi fi-${current.flag} rounded-[2px]`} aria-hidden />
        <span className="hidden sm:inline">{current.code.toUpperCase()}</span>
        <span aria-hidden className="text-[10px] opacity-60">▾</span>
      </button>
      {open && (
        <ul
          role="listbox"
          className={`absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-xl border shadow-xl ${
            light ? 'border-[#dfe5ef] bg-white' : 'border-ink-800 bg-ink-900'
          }`}
        >
          {LANGS.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                role="option"
                aria-selected={l.code === lang}
                onClick={() => {
                  setLang(l.code)
                  setOpen(false)
                }}
                className={`flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-semibold transition-colors ${
                  light
                    ? l.code === lang
                      ? 'bg-[#fff7e6] text-[#071023]'
                      : 'text-[#3d4a63] hover:bg-[#f5f7fb]'
                    : l.code === lang
                      ? 'bg-brand-600/20 text-white'
                      : 'text-ink-100 hover:bg-ink-800'
                }`}
              >
                <span className={`fi fi-${l.flag} rounded-[2px]`} aria-hidden />
                {l.native}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
